import isNumber from 'lodash/isNumber';
import { CampaignId } from 'modules/domains/campaign';
import { useMemo } from 'react';
import { useQueryEstimatedSendSize } from './useQueryEstimatedSendSize';
import { useQueryExpectedSendSize } from './useQueryExpectedSendSize';

export const useCampaignSendSizeData = (campaignId: CampaignId, campaignListLength?: number) => {
  /**
   * For Draft and Ready campaigns, we estimate the send size using the query in `useQueryEstimatedSendSize`
   * hook. For other campaign states (e.g. Scheduled, Running, Finished), we use the query in `useQueryExpectedSendSize` which is less expensive.
   * The reasoning behind having two different queries that is used based on the campaign state is that the data returned by the `useQueryExpectedSendSize`
   * is not available for Draft and Ready campaigns.
   */

  const expectedSendSizeQueryOptions = useMemo(
    () => ({
      skip: campaignId === 0,
      variables: { id: campaignId },
    }),
    [campaignId],
  );

  const expectedSendSizeData = useQueryExpectedSendSize(expectedSendSizeQueryOptions);
  const estimatedSendSizeData = useQueryEstimatedSendSize(campaignId, {
    /**
     * We only want to run this query for campaigns with send lists and that are in Draft or Ready state.
     * These campaigns won't have the `expectedSendSize` data available.
     */
    enabled: !isNumber(expectedSendSizeData?.sendSize) && !!campaignId && !!campaignListLength,
  });

  return {
    expectedSendSizeData,
    estimatedSendSizeData,
  };
};
